import GuestJourneysUpgradeBanner from "@/components/domains/GuestJourneysUpgradeBanner";
import type { DomainDashboard, DomainStatusTone, OpenIssueCounts } from "@/types";
import { formatRelativeTime } from "@/utils/formatters";

type Props = {
  domain: DomainDashboard;
  evaluating?: boolean;
};

export default function StatusGlance({ domain, evaluating = false }: Props) {
  const { open_issue_counts: counts, status, journeys_invite: invite } = domain;
  const lastCheckedLabel = lastCheckedText(domain.last_checked_at, evaluating);
  const countLine = issueCountLine(counts);

  return (
    <section className="dashboard-section">
      <div className="dashboard-section-body">
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <span className={`rounded-md px-3 py-1 text-sm font-semibold ${toneBadgeClasses(status.tone)}`}>
            {status.label}
          </span>
          {lastCheckedLabel ? <span className="text-sm text-gray-500">{lastCheckedLabel}</span> : null}
        </div>

        <div className="space-y-4">
          <div>
            <p className="text-lg leading-relaxed text-gray-800">{status.summary}</p>
            {countLine ? <p className="mt-2 text-sm text-gray-600">{countLine}</p> : null}
          </div>
          {invite ? <GuestJourneysUpgradeBanner price={invite.pro_price} upgradeUrl={invite.upgrade_url} /> : null}
        </div>
      </div>
    </section>
  );
}

function issueCountLine(counts: OpenIssueCounts) {
  if (counts.high > 0) {
    const high = `${counts.high} high-priority ${counts.high === 1 ? "issue" : "issues"}`;
    const rest = counts.total - counts.high;
    if (rest > 0) return `${high} · ${rest} more below`;

    return `${high} below`;
  }

  if (counts.total > 0) {
    return `${counts.total} ${counts.total === 1 ? "issue" : "issues"} below`;
  }

  return null;
}

function lastCheckedText(lastCheckedAt: string | null, evaluating: boolean) {
  if (evaluating) return "Updating as pages finish";
  if (lastCheckedAt) return `Last checked ${formatRelativeTime(lastCheckedAt)}`;

  return null;
}

function toneBadgeClasses(tone: DomainStatusTone) {
  if (tone === "high") return "bg-error-light text-error-dark";
  if (tone === "medium") return "bg-warning-light text-warning-dark";
  if (tone === "low") return "bg-gray-100 text-gray-600";
  if (tone === "incomplete") return "bg-warning-light text-warning-dark";

  return "bg-success-light text-success";
}
